/*!
 * index.js — /ebook-reader: the library on this device + the reading view.
 *
 * Path the page is built around: drop books → they are on the shelf (cover,
 * title, progress) → tap → reading, exactly where you stopped last time.
 * Everything else (stats, goals, notes, backup) is around that path and never
 * in its way.
 *
 * No daily cap: reading is not a run (config has no daily_runs for this slug).
 * Book files stay in this browser (db.js); "Save library" (backup.js) is the
 * way to another device or back after a cleared browser.
 */

import './polyfills.js';
import { Library } from './db.js';
import { Reader, localDay } from './reader.js';
import { ReadAloud } from './tts.js';
import { Lookup } from './lookup.js';
import { AiHelper } from './ai.js';
import { Catalog } from './catalog.js';
import { openBook, coverThumb, fileId, sniff, BookError } from './open-book.js';
import { summarize, ring, level } from './stats.js';
import { saveLibrary, restoreLibrary, estimateBytes } from './backup.js';
import { DEFAULTS } from './styles.js';

const C = window.__EB || {};
const U = C.ui || {};
const SLUG = 'ebook-reader';
const $ = (id) => document.getElementById(id);
const t = (k, r) => {
    const s = U[k] != null ? String(U[k]) : k;
    if (!r) return s;
    return s.replace(/:([a-z_]+)/g, (m, name) => (Object.prototype.hasOwnProperty.call(r, name) ? String(r[name]) : m));
};
const funnel = (stage, data) => { try { window.__aeFunnel && window.__aeFunnel(stage, data || {}); } catch (e) { /* telemetry never breaks the tool */ } };
const stage = (name) => { try { window.__stage && window.__stage(name); } catch (e) { /* telemetry */ } };
const toast = (msg, type) => { try { window.__toast ? window.__toast(msg, type || 'info') : null; } catch (e) { /* ignore */ } };
const isPro = () => { try { return !!(window.__isProNow ? window.__isProNow(false) : document.body.dataset.pro === '1'); } catch (e) { return false; } };
const locale = () => (window.__intlLocale ? window.__intlLocale() : (C.locale || 'en'));

const settingsStore = {
    load() {
        const ts = window.__toolSettings;
        const raw = ts ? ts.load(SLUG, 'reading', null) : null;
        if (raw && typeof raw === 'object') return raw;
        if (typeof raw === 'string') { try { return JSON.parse(raw); } catch (e) { /* default */ } }
        return Object.assign({}, DEFAULTS);
    },
    save(s) {
        const ts = window.__toolSettings;
        if (ts) ts.save(SLUG, 'reading', s);
        if (app.lib) app.lib.setKV('settings', s).catch(() => {});
    },
};

const app = {
    lib: null,
    reader: null,
    books: [],
    covers: new Map(),
    filter: 'all',
    sort: 'recent',
    query: '',
    menuBook: null,
    busy: false,
    speed: 0,
};

/* ── boot ─────────────────────────────────────────────────────────────── */

async function boot() {
    stage('library');
    /* The reading surface is position:fixed; an ancestor with backdrop-filter
       (the liquid-glass card) would make it fixed to THAT box. */
    const rd = $('ebReader');
    if (rd && rd.parentNode !== document.body) document.body.appendChild(rd);
    wireLibrary();
    try {
        app.lib = await Library.open();
    } catch (e) {
        showStorageProblem(e);
        return;
    }
    app.speed = await app.lib.getKV('speed', 0);
    app.reader = new Reader({
        lib: app.lib, t, cfg: C, settingsStore, isPro, toast,
        onClose: () => { stage(app.catalog && app.catalog.isShown() ? 'catalog' : 'library'); refresh(); keptOnShelfOnce(); },
        onChange: (r) => { const i = app.books.findIndex((b) => b.id === r.id); if (i >= 0) app.books[i] = r; },
        onStats: () => renderStats(),
        onSpeed: (s) => { app.speed = s; app.lib.setKV('speed', s).catch(() => {}); },
        globalSpeed: () => app.speed,
        onBeforeClose: () => { if (app.tts) app.tts.close(); if (app.lookup) app.lookup.hide(); },
        onLookup: (text) => app.lookup && app.lookup.show(text),
        onExplain: (text) => app.ai && app.ai.explain(text),
    });
    app.tts = new ReadAloud(app.reader, { t, cfg: C, toast });
    app.reader.tts = app.tts;
    app.lookup = new Lookup(app.reader, { t, cfg: C });
    app.ai = new AiHelper(app.reader, { t, cfg: C });
    app.catalog = new Catalog({
        t, locale, funnel, toast,
        pageLang: C.locale || '',
        genres: C.catalogGenres || [],
        remember: {
            get: () => (window.__toolSettings ? String(window.__toolSettings.load(SLUG, 'catalogLang', '') || '') : ''),
            set: (l) => { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'catalogLang', l); },
        },
        importBook: (file, entry) => handleFiles([file], 'catalog', { catalogId: entry.id }),
        shelfBook: (id) => app.books.find((b) => b.catalogId === id) || null,
        openBook: (rec) => openRecord(rec),
    });
    const sx = document.getElementById('ebSelExplain');
    if (sx && !(C.ai && C.ai.enabled)) sx.hidden = true;
    app.lib.onChange((msg) => {
        if (msg && msg.soft) return;
        clearTimeout(app.chT);
        // the browser closed the library under the page and it was opened again: books that were on
        // the shelf and are not in the reopened library went with the old one — the person hears it
        // now, with the same plaque as on the next visit, not from a shelf that silently shrank
        if (msg && msg.type === 'reopened') {
            const had = app.books.map((b) => b.id);
            app.chT = setTimeout(() => refresh().then(() => {
                const gone = had.filter((id) => !app.books.some((b) => b.id === id)).length;
                if (gone) showLost({ lost: gone });
            }).catch(() => {}), 250);
            return;
        }
        app.chT = setTimeout(refresh, 250);
    });
    await refresh();
    const lost = await app.lib.lostCheck(app.books.length);
    if (lost) showLost(lost);
    renderStorage();
    // Deep link from the shelf of another tab or a reload: #book=<id>
    if (location.hash === '#catalog') setTab('catalog');
    const m = location.hash.match(/book=([a-z0-9]+)/i);
    if (m) { const b = app.books.find((x) => x.id === m[1]); if (b) openRecord(b); }
}

/* ── library UI ───────────────────────────────────────────────────────── */

function wireLibrary() {
    const zone = $('ebDrop');
    if (zone) {
        const take = (file, detail) => {
            const files = (detail && detail.files && detail.files.length) ? Array.from(detail.files) : (file ? [file] : []);
            if (files.length) handleFiles(files, (detail && detail.source) || 'pick');
        };
        if (window.__bindUploaderFile) window.__bindUploaderFile(zone, take);
        else zone.addEventListener('tool-uploader:file', (e) => take(e.detail && e.detail.file, e.detail));
    }
    const q = $('ebLibSearch');
    if (q) q.addEventListener('input', () => { app.query = q.value.trim().toLowerCase(); renderShelf(); });
    const sort = $('ebLibSort');
    if (sort) {
        if (window.__toolSettings) { app.sort = window.__toolSettings.load(SLUG, 'sort', 'recent'); sort.value = app.sort; }
        sort.addEventListener('change', () => { app.sort = sort.value; if (window.__toolSettings) window.__toolSettings.save(SLUG, 'sort', app.sort); renderShelf(); });
    }
    document.querySelectorAll('[data-filter]').forEach((b) => b.addEventListener('click', () => {
        app.filter = b.dataset.filter;
        document.querySelectorAll('[data-filter]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
        renderShelf();
    }));
    const save = $('ebSave');
    if (save) save.addEventListener('click', () => onSave());
    const restore = $('ebRestore');
    if (restore) restore.addEventListener('click', () => {
        const input = zone && zone.querySelector('input[type=file]');
        if (input) input.click();
    });
    const persist = $('ebPersist');
    if (persist) persist.addEventListener('click', async () => { await app.lib.askPersist(); renderStorage(); });

    // card menu (one popover, many doors)
    document.addEventListener('click', (e) => {
        const more = e.target.closest && e.target.closest('.eb-card-more');
        if (more) app.menuBook = more.dataset.id;
    }, true);
    const menu = $('ebCardMenu');
    if (menu) menu.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        if (!b || !app.menuBook) return;
        cardAction(b.dataset.act, app.menuBook);
    });
    const goals = $('ebGoalsForm');
    if (goals) goals.addEventListener('submit', (e) => e.preventDefault());
    if (goals) goals.addEventListener('change', async () => {
        const d = Math.max(1, Math.min(600, parseInt($('ebGoalDay').value, 10) || 20));
        const y = Math.max(1, Math.min(500, parseInt($('ebGoalYear').value, 10) || 12));
        await app.lib.setKV('goal_day_min', d);
        await app.lib.setKV('goal_year_books', y);
        renderStats();
    });
    const infoDl = $('ebInfoDownload');
    if (infoDl) infoDl.addEventListener('click', () => { const id = $('ebInfoDlg').dataset.id; if (id) downloadOriginal(id); });
    const infoDel = $('ebInfoDelete');
    if (infoDel) infoDel.addEventListener('click', () => {
        const id = $('ebInfoDlg').dataset.id;
        if (!id) return;
        if (infoDel.dataset.armed !== '1') { infoDel.dataset.armed = '1'; infoDel.textContent = t('delete_confirm'); return; }
        closeInfo();
        removeBook(id);
    });
    const infoClose = $('ebInfoClose');
    if (infoClose) infoClose.addEventListener('click', () => closeInfo());
    const cont = $('ebContinue');
    if (cont) cont.addEventListener('click', () => { const id = cont.dataset.id; const b = app.books.find((x) => x.id === id); if (b) openRecord(b); });
    const sample = $('ebSample');
    if (sample) sample.addEventListener('click', () => openSample(sample));
    const sampleCat = $('ebSampleCatalog');
    if (sampleCat) sampleCat.addEventListener('click', () => setTab('catalog'));
    const tabShelf = $('ebTabShelf'), tabCat = $('ebTabCatalog');
    if (tabShelf) tabShelf.addEventListener('click', () => setTab('shelf'));
    if (tabCat) tabCat.addEventListener('click', () => setTab('catalog'));
    /* A book dropped on the open reader needs nothing here: the layout's
       off-target rescue hands any drop outside the zone to its file input, and
       one new book opens in place of the current one. */
}

/* Two tabs: the reader's own shelf and the classics catalog. The catalog list
   loads the first time its tab is shown. #catalog in the address is a link to
   the tab (replaceState: tabs are not steps of the Back button). */
function setTab(name) {
    const cat = $('ebPaneCatalog'), shelf = $('ebPaneShelf');
    if (!cat || !shelf) return;
    const toCat = name === 'catalog';
    cat.hidden = !toCat;
    shelf.hidden = toCat;
    const ts = $('ebTabShelf'), tc = $('ebTabCatalog');
    if (ts) ts.setAttribute('aria-selected', toCat ? 'false' : 'true');
    if (tc) tc.setAttribute('aria-selected', toCat ? 'true' : 'false');
    try { history.replaceState(history.state, '', toCat ? '#catalog' : location.pathname + location.search); } catch (e) { /* file:// or sandbox */ }
    if (toCat && app.catalog) { app.catalog.show(); app.catalog.render(); }
    stage(toCat ? 'catalog' : 'library');
}

/* The first time a book is closed, say where it went: one-time readers do not
   expect a shelf. */
function keptOnShelfOnce() {
    let seen = null;
    try { seen = localStorage.getItem('eb_kept_note'); } catch (e) { return; }
    if (seen) return;
    try { localStorage.setItem('eb_kept_note', '1'); } catch (e) { /* private mode */ }
    toast(t('kept_on_shelf'), 'info');
}

/* «No book at hand?» — a short public-domain classic in the page language
   (config/ebook-samples.php), added and opened like any dropped book. */
async function openSample(btn) {
    const s = C.sample;
    if (!s || !s.url || app.busy) return;
    btn.disabled = true;
    try {
        const r = await fetch(s.url, { credentials: 'same-origin' });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const blob = await r.blob();
        const file = new File([blob], s.fileName || 'sample.fb2', { type: 'application/x-fictionbook+xml' });
        funnel('sample_opened', { loc: C.locale || '' });
        await handleFiles([file], 'sample');
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'ebook_sample_fetch', { say: t('sample_failed') });
        else toast(t('sample_failed'), 'error');
    } finally {
        btn.disabled = false;
    }
}

async function refresh() {
    if (!app.lib) return;
    app.books = await app.lib.list();
    renderShelf();
    if (app.catalog && app.catalog.isShown()) app.catalog.render();
    renderStats();
    renderStorage();
}

function sortBooks(list) {
    const by = app.sort;
    const coll = new Intl.Collator(locale(), { sensitivity: 'base', numeric: true });
    const out = list.slice();
    if (by === 'title') out.sort((a, b) => coll.compare(a.title || a.fileName || '', b.title || b.fileName || ''));
    else if (by === 'author') out.sort((a, b) => coll.compare(a.author || '\uffff', b.author || '\uffff') || coll.compare(a.title || '', b.title || ''));
    else if (by === 'added') out.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
    else if (by === 'progress') out.sort((a, b) => (b.fraction || 0) - (a.fraction || 0));
    else out.sort((a, b) => (b.openedAt || b.addedAt || 0) - (a.openedAt || a.addedAt || 0));
    return out;
}

function matches(b) {
    if (app.filter === 'reading' && !(b.fraction > 0 && !b.finishedAt)) return false;
    if (app.filter === 'unread' && (b.fraction > 0 || b.finishedAt)) return false;
    if (app.filter === 'finished' && !b.finishedAt) return false;
    if (app.query) {
        const hay = ((b.title || '') + ' ' + (b.author || '') + ' ' + (b.series && b.series.name || '') + ' ' + (b.fileName || '')).toLowerCase();
        if (hay.indexOf(app.query) === -1) return false;
    }
    return true;
}

function hueOf(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h % 360;
}

async function coverUrl(b) {
    if (!b.hasCover) return null;
    if (app.covers.has(b.id)) return app.covers.get(b.id);
    const blob = await app.lib.cover(b.id);
    const url = blob ? URL.createObjectURL(blob) : null;
    app.covers.set(b.id, url);
    return url;
}

function coverEl(b) {
    const span = document.createElement('span');
    span.className = 'eb-cover';
    const title = b.title || b.fileName || '';
    span.style.setProperty('--eb-hue', String(hueOf(title)));
    const ph = document.createElement('span');
    ph.className = 'eb-cover-ph';
    const pt = document.createElement('b'); pt.textContent = title.slice(0, 80);
    const pa = document.createElement('i'); pa.textContent = (b.author || '').slice(0, 60);
    ph.append(pt, pa);
    span.append(ph);
    if (b.hasCover) {
        coverUrl(b).then((url) => {
            if (!url) return;
            const img = document.createElement('img');
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.src = url;
            img.addEventListener('load', () => span.classList.add('has-img'));
            span.append(img);
        });
    }
    const badge = document.createElement('em');
    badge.className = 'eb-fmt';
    badge.textContent = (b.formatLabel || b.format || '').toUpperCase();
    span.append(badge);
    return span;
}

function pctOf(b) { return Math.min(100, Math.round((b.fraction || 0) * 100)); }

function renderShelf() {
    const shelf = $('ebShelf');
    const empty = $('ebEmpty');
    const tools = $('ebLibTools');
    if (!shelf) return;
    const total = app.books.length;
    if (empty) empty.hidden = total > 0;
    if (tools) tools.hidden = total === 0;
    const list = sortBooks(app.books.filter(matches));
    shelf.replaceChildren();
    const frag = document.createDocumentFragment();
    for (const b of list) {
        const card = document.createElement('article');
        card.className = 'eb-card';
        card.dataset.id = b.id;
        if (b.finishedAt) card.classList.add('is-done');
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'eb-card-open';
        open.setAttribute('aria-label', t('open_book', { title: b.title || b.fileName || '' }));
        open.append(coverEl(b));
        const body = document.createElement('span');
        body.className = 'eb-card-body';
        const ti = document.createElement('b'); ti.className = 'eb-card-title'; ti.textContent = b.title || b.fileName || '';
        const au = document.createElement('span'); au.className = 'eb-card-author'; au.textContent = b.author || '';
        body.append(ti, au);
        if (b.series && b.series.name) {
            const se = document.createElement('span'); se.className = 'eb-card-series';
            se.textContent = b.series.name + (b.series.position ? ' · ' + b.series.position : '');
            body.append(se);
        }
        const bar = document.createElement('span'); bar.className = 'eb-card-prog';
        const fill = document.createElement('i'); fill.style.width = pctOf(b) + '%'; bar.append(fill);
        const meta = document.createElement('span'); meta.className = 'eb-card-meta';
        meta.textContent = b.finishedAt ? t('state_finished') : (b.fraction > 0 ? pctOf(b) + '%' : t('state_new'));
        body.append(bar, meta);
        open.append(body);
        open.addEventListener('click', () => openRecord(b));
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'eb-card-more';
        more.dataset.id = b.id;
        more.setAttribute('popovertarget', 'ebCardMenu');
        more.setAttribute('aria-label', t('book_actions'));
        more.textContent = '⋯';
        card.append(open, more);
        frag.append(card);
    }
    shelf.append(frag);
    const none = $('ebShelfNone');
    if (none) none.hidden = !(total > 0 && list.length === 0);
    const count = $('ebLibCount');
    if (count) count.textContent = total ? t('books_count', { n: total }) : '';
    const tabN = $('ebTabShelfN');
    if (tabN) { tabN.textContent = String(total); tabN.hidden = total === 0; }
    renderContinue();
    // drop object URLs of books no longer on the shelf
    for (const [id, url] of app.covers) if (!app.books.some((b) => b.id === id)) { if (url) URL.revokeObjectURL(url); app.covers.delete(id); }
}

function renderContinue() {
    const box = $('ebContinue');
    if (!box) return;
    const b = app.books.filter((x) => x.openedAt && !x.finishedAt && x.fraction > 0).sort((a, c) => c.openedAt - a.openedAt)[0];
    if (!b) { box.hidden = true; return; }
    box.hidden = false;
    box.dataset.id = b.id;
    $('ebContinueTitle').textContent = b.title || b.fileName || '';
    $('ebContinueMeta').textContent = [b.chapter, pctOf(b) + '%'].filter(Boolean).join(' · ');
    const cv = $('ebContinueCover');
    cv.replaceChildren(coverEl(b));
}

/* ── stats ────────────────────────────────────────────────────────────── */

async function renderStats() {
    const box = $('ebStats');
    if (!box || !app.lib) return;
    const [days, gd, gy] = await Promise.all([app.lib.days(), app.lib.getKV('goal_day_min', 20), app.lib.getKV('goal_year_books', 12)]);
    const s = summarize(days, app.books, { day: gd, year: gy });
    box.hidden = !(app.books.length || days.length);
    $('ebGoalDay').value = String(gd);
    $('ebGoalYear').value = String(gy);
    const today = $('ebStatToday');
    today.replaceChildren(ring(s.todayMin / Math.max(1, s.dayGoal)));
    const tt = document.createElement('span');
    const big = document.createElement('b'); big.textContent = t('min_short', { n: s.todayMin });
    const small = document.createElement('small'); small.textContent = t('stat_today_of', { goal: s.dayGoal });
    tt.append(big, small);
    today.append(tt);
    const streak = $('ebStatStreak');
    streak.replaceChildren();
    const sb = document.createElement('b'); sb.textContent = String(s.streak);
    const ss = document.createElement('small'); ss.textContent = t('stat_streak', { n: s.streak, best: s.longest });
    streak.append(sb, ss);
    const year = $('ebStatYear');
    year.replaceChildren(ring(s.finishedThisYear / Math.max(1, s.yearGoal)));
    const ys = document.createElement('span');
    const yb = document.createElement('b'); yb.textContent = s.finishedThisYear + ' / ' + s.yearGoal;
    const yl = document.createElement('small'); yl.textContent = t('stat_year');
    ys.append(yb, yl);
    year.append(ys);
    const week = $('ebStatWeek');
    week.textContent = t('stat_week', { time: fmtMin(s.weekMin), total: fmtMin(s.totalMin) });
    const heat = $('ebHeat');
    heat.replaceChildren();
    const fmt = new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' });
    for (const c of s.heat) {
        const i = document.createElement('i');
        i.dataset.l = String(level(c.min));
        i.style.gridRow = String(c.dow + 1);
        i.title = fmt.format(new Date(c.day + 'T12:00:00')) + ' — ' + t('min_short', { n: c.min });
        heat.append(i);
    }
}

function fmtMin(min) {
    if (min < 60) return t('min_short', { n: min });
    const h = Math.floor(min / 60), m = min % 60;
    return m ? t('h_min_short', { h, m }) : t('h_short', { h });
}

/* ── storage status ───────────────────────────────────────────────────── */

async function renderStorage() {
    const line = $('ebStoreLine');
    if (!line || !app.lib) return;
    const n = app.books.length;
    const bytes = app.books.reduce((a, b) => a + (b.size || 0), 0);
    const prot = await app.lib.protection();
    const mb = bytes / 1048576;
    line.textContent = n ? t('store_line', { n, size: mb < 10 ? mb.toFixed(1) : Math.round(mb) }) : t('store_empty');
    const p = $('ebStoreProt');
    if (p) {
        p.textContent = prot.persisted ? t('store_persisted') : t('store_not_persisted');
        p.dataset.ok = prot.persisted ? '1' : '0';
    }
    const ask = $('ebPersist');
    if (ask) ask.hidden = prot.persisted || !n || !(navigator.storage && navigator.storage.persist);
    const ios = $('ebIosHint');
    if (ios) {
        const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const standalone = (window.matchMedia && matchMedia('(display-mode: standalone)').matches) || navigator.standalone;
        ios.hidden = !(isIOS && !standalone && n);
    }
    const save = $('ebSave');
    if (save) save.disabled = !n;
}

function showStorageProblem(e) {
    const box = $('ebStoreProblem');
    if (box) { box.hidden = false; box.textContent = t('err_storage_open'); }
    try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'library_open_failed', err: String(e && (e.name || e.message) || e).slice(0, 60) }); } catch (_) { /* telemetry */ }
}

function showLost(lost) {
    const box = $('ebLost');
    if (!box) return;
    box.hidden = false;
    $('ebLostText').textContent = t('lost_text', { n: lost.lost });
    funnel('library_lost', { n: lost.lost });
}

/* ── import ───────────────────────────────────────────────────────────── */

async function isBackup(files) {
    if (files.some((f) => f.name === 'library.json')) return true;
    if (files.length === 1 && /\.zip$/i.test(files[0].name)) {
        try {
            const s = await sniff(files[0]);
            // Any zip kind: a backup holding book-*.fb2 sniffs as fbz.
            return !!(s.loader && s.loader.has('library.json'));
        } catch (e) { return false; }
    }
    return false;
}

async function handleFiles(files, source, extra) {
    if (!app.lib) { toast(t('err_storage_open'), 'error'); return; }
    if (app.busy) { toast(t('busy'), 'info'); return; }
    app.busy = true;
    const prog = $('ebImport');
    try {
        if (await isBackup(files)) { await doRestore(files); return; }
        const added = [];
        let lastFailed = null;
        if (prog) { prog.hidden = false; }
        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            if (prog) $('ebImportText').textContent = t('importing', { i: i + 1, n: files.length, name: f.name });
            try {
                const rec = await addOne(f, source, extra);
                if (rec) added.push(rec);
            } catch (e) {
                lastFailed = { e, f };
                reportOpenError(e, f, 'import');
                if (files.length === 1) toast(errorText(e, f), 'error');
            }
        }
        if (prog) prog.hidden = true;
        await refresh();
        if (added.length && !added[0].__existing) app.lib.askPersist().then(() => renderStorage());
        if (files.length > 1) {
            const failed = files.length - added.length;
            toast(failed ? t('imported_some', { n: added.length, failed }) : t('imported_n', { n: added.length }), failed ? 'warning' : 'success');
            if (lastFailed && failed === 1) toast(errorText(lastFailed.e, lastFailed.f), 'error');
        } else if (added.length === 1) {
            openRecord(added[0]);
        }
    } finally {
        app.busy = false;
        if (prog) prog.hidden = true;
    }
}

async function addOne(file, source, extra) {
    const t0 = performance.now();
    const id = await fileId(file);
    const existing = await app.lib.get(id);
    if (existing) {
        // the same book dropped earlier and now taken from the catalog: the card should know it is on the shelf
        if (extra && extra.catalogId && !existing.catalogId) { const upd = await app.lib.patch(id, { catalogId: extra.catalogId }); if (upd) Object.assign(existing, upd); }
        existing.__existing = true;
        return existing;
    }
    const opened = await openBook(file, { pdfSources: C.pdfjs, djvuWorker: C.djvuWorker, archive: C.archive });
    const cover = await coverThumb(opened.book);
    try { opened.book.destroy && opened.book.destroy(); } catch (e) { /* ignore */ }
    const rec = {
        id,
        title: opened.meta.title || prettyName(file.name),
        author: opened.meta.author || '',
        series: opened.meta.series || null,
        language: opened.meta.language || '',
        description: opened.meta.description || '',
        publisher: opened.meta.publisher || '',
        format: opened.format,
        formatLabel: opened.formatLabel,
        fixed: !!opened.meta.fixed,
        pages: opened.meta.pages || 0,
        fileName: file.name,
        size: file.size,
        addedAt: Date.now(),
        openedAt: 0,
        fraction: 0,
        cfi: null,
        timeMs: 0,
    };
    if (extra && extra.catalogId) rec.catalogId = String(extra.catalogId);
    try {
        await app.lib.add(rec, file, cover);
    } catch (e) {
        const err = new BookError(e && e.name === 'QuotaExceededError' ? 'STORAGE_FULL' : 'STORAGE_WRITE');
        err.cause = e;
        throw err;
    }
    funnel('book_added', { fmt: opened.format, mb: Math.round(file.size / 104857.6) / 10, ms: Math.round(performance.now() - t0), src: source || '' });
    return rec;
}

function prettyName(name) {
    return String(name || '').replace(/\.[a-z0-9]{1,5}$/i, '').replace(/\.(fb2)$/i, '').replace(/[_]+/g, ' ').trim();
}

function errorText(e, f) {
    const code = (e && e.code) || '';
    const name = (f && f.name) || '';
    const d = (e && e.detail) || {};
    switch (code) {
        case 'DRM': return t('err_drm', { format: d.format || '', name });
        case 'ACSM': return t('err_acsm');
        case 'PDF_PASSWORD': return t('err_pdf_password', { name });
        case 'EMPTY': return t('err_empty', { name });
        case 'DJVU_INDIRECT': return t('err_djvu_indirect', { name });
        case 'ARCHIVE_PASSWORD': return t('err_archive_password', { name });
        case 'UNSUPPORTED': return t('err_unsupported', { format: String(d.kind || '').toUpperCase(), name });
        case 'STORAGE_FULL': return t('err_storage_full');
        case 'STORAGE_WRITE': return t('err_storage_write');
        // the browser refused the book's own frame (WebKit + CSP, 16.10) — not a damaged file
        case 'FRAME_NO_DOCUMENT': return t('err_frame', { name });
        default: return t('err_broken', { name });
    }
}

function reportOpenError(e, f, where) {
    try {
        window.__aeDiagnostic && window.__aeDiagnostic({
            issue: 'book_open_failed', where, code: (e && e.code) || 'ERR', ext: String((f && f.name) || '').split('.').pop().toLowerCase().slice(0, 8),
            mb: f ? Math.round(f.size / 1048576) : 0, err: String((e && e.detail && e.detail.cause) || (e && e.message) || '').slice(0, 80),
        });
    } catch (_) { /* telemetry */ }
}

/* ── open / actions ───────────────────────────────────────────────────── */

async function openRecord(b) {
    if (app.opening) return;
    app.opening = true;
    const t0 = performance.now();
    try {
        const file = await app.lib.file(b.id);
        if (!file) { toast(t('err_file_missing'), 'error'); return; }
        stage('reading');
        const opened = await app.reader.open(b, file);
        const returning = b.openedAt ? 1 : 0;
        funnel('book_opened', { fmt: opened.format, returning, lib: app.books.length, ms: Math.round(performance.now() - t0) });
        try { window.__aeToolComplete && window.__aeToolComplete({ fmt: opened.format, returning, lib: app.books.length }); } catch (e) { /* telemetry */ }
    } catch (e) {
        stage('library');
        reportOpenError(e, { name: b.fileName, size: b.size }, 'open');
        toast(errorText(e, { name: b.fileName }), 'error');
        try { await app.reader.close({ silent: true }); } catch (_) { /* ignore */ }
    } finally {
        app.opening = false;
    }
}

function cardAction(act, id) {
    const b = app.books.find((x) => x.id === id);
    if (!b) return;
    if (act === 'open') openRecord(b);
    else if (act === 'info') openInfo(b);
    else if (act === 'finished') app.lib.patch(id, { finishedAt: b.finishedAt ? 0 : Date.now(), fraction: b.finishedAt ? b.fraction : Math.max(b.fraction || 0, 1) }).then(refresh);
    else if (act === 'reset') app.lib.patch(id, { finishedAt: 0, fraction: 0, cfi: null, chapter: '' }).then(refresh);
    else if (act === 'download') downloadOriginal(id);
    else if (act === 'delete') openInfo(b, true);
}

async function downloadOriginal(id) {
    const b = app.books.find((x) => x.id === id);
    const f = await app.lib.file(id);
    if (!f || !b) return;
    if (window.__downloadFile) await window.__downloadFile(f, b.fileName || ('book.' + (b.format || 'bin')));
}

async function removeBook(id) {
    await app.lib.remove(id);
    const url = app.covers.get(id);
    if (url) URL.revokeObjectURL(url);
    app.covers.delete(id);
    await refresh();
    toast(t('book_deleted'), 'info');
}

async function openInfo(b, armDelete) {
    const dlg = $('ebInfoDlg');
    dlg.dataset.id = b.id;
    $('ebInfoTitle').textContent = b.title || b.fileName || '';
    $('ebInfoAuthor').textContent = b.author || '';
    const dl = $('ebInfoList');
    dl.replaceChildren();
    const df = new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'long', year: 'numeric' });
    const notes = await app.lib.notes(b.id);
    const rows = [
        [t('info_format'), (b.formatLabel || b.format || '').toUpperCase()],
        [t('info_file'), b.fileName || ''],
        [t('info_size'), (b.size / 1048576).toFixed(b.size < 10485760 ? 1 : 0) + ' MB'],
        b.series && b.series.name ? [t('info_series'), b.series.name + (b.series.position ? ' · ' + b.series.position : '')] : null,
        b.language ? [t('info_language'), b.language] : null,
        b.publisher ? [t('info_publisher'), b.publisher] : null,
        [t('info_added'), df.format(new Date(b.addedAt))],
        b.openedAt ? [t('info_opened'), df.format(new Date(b.openedAt))] : null,
        [t('info_progress'), b.finishedAt ? t('state_finished') : pctOf(b) + '%'],
        b.timeMs ? [t('info_time'), fmtMin(Math.round(b.timeMs / 60000))] : null,
        notes.length ? [t('info_notes'), String(notes.length)] : null,
    ].filter(Boolean);
    for (const [k, v] of rows) {
        const dt = document.createElement('dt'); dt.textContent = k;
        const dd = document.createElement('dd'); dd.textContent = v;
        dl.append(dt, dd);
    }
    const desc = $('ebInfoDesc');
    desc.textContent = b.description || '';
    desc.hidden = !b.description;
    const del = $('ebInfoDelete');
    del.dataset.armed = armDelete ? '1' : '0';
    del.textContent = armDelete ? t('delete_confirm') : t('delete_book');
    if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
}

function closeInfo() {
    const dlg = $('ebInfoDlg');
    if (dlg.close) dlg.close(); else dlg.removeAttribute('open');
}

/* ── backup ───────────────────────────────────────────────────────────── */

function onSave() {
    if (!app.lib || !app.books.length) return;
    const books = app.books.slice();
    const zipName = (window.__outputName && window.__outputName.join ? window.__outputName.join([t('backup_name'), localDay()], 'zip') : 'library-' + localDay() + '.zip');
    // saveLibrary's first await is the folder picker — nothing may be awaited before it.
    saveLibrary(app.lib, books, { zipName, settings: settingsStore.load() }).then((res) => {
        funnel('library_saved', { n: books.length, via: res.via, books: res.withBooks ? 1 : 0, mb: Math.round(estimateBytes(books) / 1048576) });
        if (res.via === 'refused') return;
        if (!res.withBooks) toast(t('saved_without_books'), 'warning');
        else if (res.via === 'folder') toast(t('saved_folder', { n: res.written }), 'success');
    }).catch((e) => {
        if (window.__handleError) window.__handleError(e, 'ebook_backup_save', { say: t('save_failed') });
        else toast(t('save_failed'), 'error');
    });
}

async function doRestore(files) {
    const prog = $('ebImport');
    if (prog) { prog.hidden = false; $('ebImportText').textContent = t('restoring'); }
    try {
        const res = await restoreLibrary(app.lib, files, { onProgress: (i, n) => { if (prog) $('ebImportText').textContent = t('restoring_n', { i, n }); } });
        if (res.settings && app.reader) settingsStore.save(Object.assign({}, DEFAULTS, res.settings));
        await refresh();
        const lost = $('ebLost'); if (lost) lost.hidden = true;
        toast(t('restored', { n: res.books, notes: res.notes }) + (res.missingFiles ? ' ' + t('restored_missing', { n: res.missingFiles }) : ''), res.missingFiles ? 'warning' : 'success');
        funnel('library_restored', { n: res.books, notes: res.notes, missing: res.missingFiles, skipped: res.skipped });
        app.lib.askPersist().then(() => renderStorage());
    } catch (e) {
        const code = e && e.code;
        toast(code === 'NO_MANIFEST' || code === 'BAD_MANIFEST' ? t('restore_bad') : t('restore_failed'), 'error');
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'library_restore_failed', code: code || 'ERR' }); } catch (_) { /* telemetry */ }
    } finally {
        if (prog) prog.hidden = true;
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
